import {i18n} from '@yourcoach/i18n';

import type {ConferenceApiModel} from '../../../api/conference';
import type {CoreCoachConferencesStartArchiveParams} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig} from '../utils';

import {conferenceStore} from '../../../api/conference';
import RecordIcon from '../../../assets/icons/primary/Record.svg';
import {alert} from '../../../components/alert';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {envManager} from '../../../utils/env';
import {AppError} from '../../../utils/errors/app';
import {COMMAND, isCancelCommand} from '../utils';

export {conferenceStartRecord};

type Config = BaseConfig<
  Omit<
    CoreCoachConferencesStartArchiveParams,
    '_id' | 'store_plot' | 'store_video'
  >
> & {
  entity: ConferenceApiModel;
  isCorporate: boolean;
};

const strategy: AsyncStrategy = async (roles, config) => {
  return isMeCoachForEntity((config as Config).entity)
    ? roles.coach(config)
    : roles.client(config);
};

async function conferenceStartRecord(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

conferenceStartRecord.asClient = asClient;
conferenceStartRecord.asCoach = asCoach;

type ConfirmResult = 'video' | 'video+transcription' | symbol;

async function asClient(_config: Config) {
  throw new AppError('not_allowed');
}

async function asCoach({entity, isCorporate, params}: Config) {
  let recordType: ConfirmResult =
    isCorporate && envManager.isCore ? 'video+transcription' : 'video';

  if (!isCorporate) {
    recordType = await confirm();

    if (isCancelCommand(recordType)) {
      return;
    }
  }

  const recordSettings: Pick<
    Required<CoreCoachConferencesStartArchiveParams>,
    'store_plot' | 'store_video'
  > = {
    store_plot: false,
    store_video: true,
  };

  if (recordType === 'video+transcription') {
    recordSettings.store_plot = true;
  }

  const conference = await conferenceStore.coach.startArchive(entity, {
    b2b: {
      ...params,
    },
    core: {
      ...params,
      ...recordSettings,
    },
  });

  return conference;
}

const confirm = () =>
  alert.show<ConfirmResult>({
    buttons: [
      {
        label: i18n.t(
          'shared:services.entity_action.conference.start_record.confirm.button.video_only',
        ),
        onAwaitableClick: resolve => resolve('video'),
        scheme: 'error',
      },
      {
        label: i18n.t(
          'shared:services.entity_action.conference.start_record.confirm.button.video_with_transcription',
        ),
        onAwaitableClick: resolve => resolve('video+transcription'),
        scheme: 'gray',
      },
    ],
    icon: RecordIcon,
    iconColor: 'icon.red',
    isDismissible: true,
    onAwaitableDismiss: resolve => resolve(COMMAND.CANCEL),
    title: i18n.t(
      'shared:services.entity_action.conference.start_record.confirm.title',
    ),
    variant: 'promo',
  });
